import { PanelTitle } from '../layout/Typography';

interface SidebarHeaderProps {
  title: string;
  /** Action buttons rendered on the right side of the title row. */
  actions?: React.ReactNode;
  /**
   * Optional content below the title row (search bars, filter bars, etc.).
   * Receives its own padding — wrap in `px-3 py-2` for standard spacing.
   */
  children?: React.ReactNode;
  /**
   * Optional docs slug forwarded to `PanelTitle.docs`. Supports the
   * `slug#anchor` form (e.g. `concepts/agents` or
   * `plugins/enabling-plugins#prerequisites`) so list-page headers can deep-link
   * into a specific section of the docs.
   */
  docs?: string;
  /**
   * Optional inline badge rendered beside the title — use for maturity markers
   * (alpha/beta) or other inline status indicators.
   */
  badge?: React.ReactNode;
}

/**
 * Shared header for sidebar panels in TwoPanelLayout pages.
 * The title row uses `h-11` to align vertically with `PanelHeader` in the
 * adjacent detail panel.
 */
export function SidebarHeader({ title, actions, children, docs, badge }: SidebarHeaderProps) {
  return (
    <div className="shrink-0">
      <div className="flex h-11 items-center justify-between border-b border-border/30 px-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <PanelTitle as="h2" size="sm" docs={docs}>
            {title}
          </PanelTitle>
          {badge && <span className="inline-flex shrink-0 items-center">{badge}</span>}
        </div>
        {actions && <div className="flex items-center gap-1">{actions}</div>}
      </div>
      {/* Content-top slot for plugin-contributed banners (e.g. guided
          tutorials). Sits directly below the panel title and above the
          sidebar's search/filter row, which renders as `children` or a sibling
          after this header. `empty:hidden` collapses it to nothing when no
          banner is portaled in. */}
      <div
        data-page-banner-region=""
        className="border-b border-border/40 p-2 empty:hidden empty:border-b-0 empty:p-0"
      />
      {children}
    </div>
  );
}
